import * as React from "react";
import {
  fetchAdminLanguages,
  fetchModulesForLanguage,
  type Language,
} from "../api/localization";
import { useWebSocket } from "../useWebSocket";
import { defaultLangCode } from "./trainingLocalizedDraft";

/** Ignore websocket revision bumps briefly after a local save (echo suppression). */
let suppressRemoteRevisionUntil = 0;

/** Call before/after local certification loc writes to avoid remount/refetch churn from our own WS echo. */
export function suppressCertificationLocRemoteRevision(ms = 2500) {
  suppressRemoteRevisionUntil = Date.now() + ms;
}

/**
 * Shared admin languages + certification-content definitions loader for
 * LocalizedField forms on the module detail Course section.
 *
 * Soft-refreshes definitions on `entity:localization:*` certification events
 * without flipping `ready` to false (avoids blank remounts).
 */
export function useCertificationLocLanguages(accessToken: string | null | undefined) {
  const { subscribe, unsubscribe } = useWebSocket();
  const [languages, setLanguages] = React.useState<Language[]>([]);
  const [defaultLang, setDefaultLang] = React.useState("en");
  const [defsByLang, setDefsByLang] = React.useState<Record<string, Record<string, string>>>({});
  const [ready, setReady] = React.useState(false);
  /** Increments only on websocket-driven refreshes (not initial load / own-save echo). */
  const [remoteRevision, setRemoteRevision] = React.useState(0);
  const loadedLangsRef = React.useRef<Set<string>>(new Set());
  const defsByLangRef = React.useRef(defsByLang);
  defsByLangRef.current = defsByLang;
  const accessTokenRef = React.useRef(accessToken);
  accessTokenRef.current = accessToken;
  const hadContentRef = React.useRef(false);

  React.useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    // Soft reload when the token refreshes: keep ready=true and existing defs so
    // ModuleDetailPage does not tear down DetailPageLayout (scroll jump / remount).
    const soft = hadContentRef.current;
    if (!soft) {
      loadedLangsRef.current = new Set();
      setDefsByLang({});
      setReady(false);
      setRemoteRevision(0);
    }

    (async () => {
      try {
        const langs = await fetchAdminLanguages(accessToken);
        if (cancelled) return;
        setLanguages(langs);
        const defLang = defaultLangCode(langs);
        setDefaultLang(defLang);

        const modules = await fetchModulesForLanguage(accessToken, defLang);
        if (cancelled) return;
        const cert = modules.find((m) => m.context === "certification");
        const definitions = cert?.definitions ?? {};
        loadedLangsRef.current.add(defLang);
        setDefsByLang((prev) => (soft ? { ...prev, [defLang]: definitions } : { [defLang]: definitions }));
        hadContentRef.current = true;
      } catch {
        // Keep empty languages; fields still render without the flag menu.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const refreshLangDefinitions = React.useCallback(async (lang: string) => {
    const token = accessTokenRef.current;
    if (!token) return;
    try {
      const modules = await fetchModulesForLanguage(token, lang);
      const cert = modules.find((m) => m.context === "certification");
      const definitions = cert?.definitions ?? {};
      loadedLangsRef.current.add(lang);
      setDefsByLang((prev) => ({ ...prev, [lang]: definitions }));
    } catch {
      // Keep previous definitions on refresh failure.
    }
  }, []);

  React.useEffect(() => {
    const handler = (msg: { channel?: string }) => {
      const channel = msg.channel ?? "";
      const parts = channel.split(":");
      if (parts[0] !== "entity" || parts[1] !== "localization") return;
      if (parts[2] === "languages") return;
      const lang = parts[2];
      const context = parts[3];
      if (!lang || context !== "certification") return;
      // Only soft-refresh languages we've already loaded for the form.
      if (!loadedLangsRef.current.has(lang)) return;
      const suppressRevision = Date.now() < suppressRemoteRevisionUntil;
      void refreshLangDefinitions(lang).then(() => {
        // Own-save echoes still refresh defs quietly; skip revision to avoid
        // remount/refetch/dirty-resurrection loops in the editor.
        if (!suppressRevision) {
          setRemoteRevision((n) => n + 1);
        }
      });
    };
    subscribe(["entity:localization:*"], handler);
    return () => unsubscribe(["entity:localization:*"], handler);
  }, [subscribe, unsubscribe, refreshLangDefinitions]);

  const ensureLangDefinitions = React.useCallback(
    async (lang: string): Promise<Record<string, string>> => {
      if (!accessToken) return {};
      if (loadedLangsRef.current.has(lang)) return defsByLangRef.current[lang] ?? {};
      try {
        const modules = await fetchModulesForLanguage(accessToken, lang);
        const cert = modules.find((m) => m.context === "certification");
        const definitions = cert?.definitions ?? {};
        loadedLangsRef.current.add(lang);
        setDefsByLang((prev) => ({ ...prev, [lang]: definitions }));
        return definitions;
      } catch {
        loadedLangsRef.current.add(lang);
        return {};
      }
    },
    [accessToken],
  );

  return {
    languages,
    defaultLang,
    defsByLang,
    ready,
    remoteRevision,
    ensureLangDefinitions,
  };
}
